import React, { useCallback, useEffect } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import Highlight from '@tiptap/extension-highlight'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import { FontFamily } from '@tiptap/extension-font-family'
import { Icon } from '../../atoms/Icon'
import { ColorPicker } from './components/ColorPicker'
import { FontSizeDropdown } from './components/FontSizeDropdown'
import { EmojiPicker } from './components/EmojiPicker'
import { ImageUploader } from './components/ImageUploader'
import { LinkModal } from './components/LinkModal'
import { RichTextEditorProps } from './RichTextEditor.types'

const TextStyleWithFontSize = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: (element) => (element as HTMLElement).style.fontSize || null,
        renderHTML: (attributes) => {
          if (!attributes.fontSize) return {}
          return { style: `font-size: ${attributes.fontSize}` }
        },
      },
    }
  },
})
import {
  StyledEditorContainer,
  StyledToolbar,
  StyledToolbarGroup,
  StyledToolbarSeparator,
  StyledToolbarButton,
  StyledEditorArea,
  StyledFooter,
  StyledCharCount,
  StyledHeadingDropdown,
} from './RichTextEditor.styles'

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = '',
  onChange,
  placeholder = 'Start typing...',
  disabled = false,
  readOnly = false,
  height,
  minHeight = 120,
  maxHeight,
  showToolbar = true,
  className,
  error = false,
  onFocus,
  onBlur,
  autoFocus = false,
  maxLength,
  showCharCount = false,
  features = {
    bold: true,
    italic: true,
    strikethrough: true,
    headings: true,
    lists: true,
    links: true,
    highlighting: true,
    alignment: true,
    fontSize: true,
    fontColor: true,
    emojis: true,
    images: true,
    undoRedo: false,
  },
}) => {
  const [showLinkModal, setShowLinkModal] = React.useState(false)
  const [showColorPicker, setShowColorPicker] = React.useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = React.useState(false)
  const [showImageUploader, setShowImageUploader] = React.useState(false)
  const [charCount, setCharCount] = React.useState(0)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Highlight.configure({
        multicolor: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      TextStyleWithFontSize,
      Color,
      FontFamily,
    ],
    content: value,
    editable: !disabled && !readOnly,
    autofocus: autoFocus,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      onChange?.(html)
      setCharCount(
        editor.storage.characterCount?.characters() || editor.getText().length
      )
    },
    onFocus: () => {
      onFocus?.()
    },
    onBlur: () => {
      onBlur?.()
    },
  })

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value)
    }
  }, [value, editor])

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled && !readOnly)
    }
  }, [disabled, readOnly, editor])

  const handleHeadingChange = useCallback(
    (level: number) => {
      if (!editor) return
      if (level === 0) {
        editor.chain().focus().setParagraph().run()
      } else {
        editor
          .chain()
          .focus()
          .toggleHeading({ level: level as 1 | 2 | 3 | 4 })
          .run()
      }
    },
    [editor]
  )

  const handleAlignment = useCallback(
    (align: string) => {
      if (!editor) return
      editor.chain().focus().setTextAlign(align).run()
    },
    [editor]
  )

  const handleAddLink = useCallback(
    (url: string, text?: string) => {
      if (!editor) return

      if (text && editor.state.selection.empty) {
        editor
          .chain()
          .focus()
          .insertContent(`<a href="${url}" target="_blank">${text}</a>`)
          .run()
      } else {
        editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .setLink({ href: url })
          .run()
      }

      setShowLinkModal(false)
    },
    [editor]
  )

  const handleColorChange = useCallback(
    (color: string) => {
      if (!editor) return
      editor.chain().focus().setColor(color).run()
      setShowColorPicker(false)
    },
    [editor]
  )

  const handleHighlight = useCallback(() => {
    if (!editor) return
    editor.chain().focus().toggleHighlight().run()
  }, [editor])

  const handleEmojiInsert = useCallback(
    (emoji: string) => {
      if (!editor) return
      editor.chain().focus().insertContent(emoji).run()
      setShowEmojiPicker(false)
    },
    [editor]
  )

  const handleImageInsert = useCallback(
    (url: string) => {
      if (!editor) return
      editor.chain().focus().setImage({ src: url }).run()
      setShowImageUploader(false)
    },
    [editor]
  )

  const handleFontSizeChange = useCallback(
    (size: string) => {
      if (!editor) return
      editor.chain().focus().setMark('textStyle', { fontSize: size }).run()
    },
    [editor]
  )

  const getHeadingLevel = () => {
    if (!editor) return 0
    if (editor.isActive('heading', { level: 1 })) return 1
    if (editor.isActive('heading', { level: 2 })) return 2
    if (editor.isActive('heading', { level: 3 })) return 3
    if (editor.isActive('heading', { level: 4 })) return 4
    return 0
  }

  const getCurrentAlignment = () => {
    if (!editor) return 'left'
    if (editor.isActive({ textAlign: 'center' })) return 'center'
    if (editor.isActive({ textAlign: 'right' })) return 'right'
    if (editor.isActive({ textAlign: 'justify' })) return 'justify'
    return 'left'
  }

  if (!editor) return null

  return (
    <StyledEditorContainer
      className={className}
      $disabled={disabled}
      $error={error}
      $focused={editor.isFocused}
    >
      {showToolbar && !readOnly && (
        <StyledToolbar>
          {features.headings && (
            <>
              <StyledToolbarGroup>
                <StyledHeadingDropdown
                  value={getHeadingLevel()}
                  onChange={(e) => handleHeadingChange(Number(e.target.value))}
                  disabled={disabled}
                >
                  <option value={0}>Normal</option>
                  <option value={1}>Heading 1</option>
                  <option value={2}>Heading 2</option>
                  <option value={3}>Heading 3</option>
                  <option value={4}>Heading 4</option>
                </StyledHeadingDropdown>
              </StyledToolbarGroup>
              <StyledToolbarSeparator />
            </>
          )}

          <StyledToolbarGroup>
            {features.bold && (
              <StyledToolbarButton
                type="button"
                onClick={() => editor.chain().focus().toggleBold().run()}
                $active={editor.isActive('bold')}
                $disabled={disabled}
                disabled={disabled}
                title="Bold (Ctrl+B)"
              >
                <Icon name="bold" size="small" />
              </StyledToolbarButton>
            )}
            {features.italic && (
              <StyledToolbarButton
                type="button"
                onClick={() => editor.chain().focus().toggleItalic().run()}
                $active={editor.isActive('italic')}
                $disabled={disabled}
                disabled={disabled}
                title="Italic (Ctrl+I)"
              >
                <Icon name="italic" size="small" />
              </StyledToolbarButton>
            )}
            {features.strikethrough && (
              <StyledToolbarButton
                type="button"
                onClick={() => editor.chain().focus().toggleStrike().run()}
                $active={editor.isActive('strike')}
                $disabled={disabled}
                disabled={disabled}
                title="Strikethrough"
              >
                <Icon name="strikethrough" size="small" />
              </StyledToolbarButton>
            )}
            {features.highlighting && (
              <StyledToolbarButton
                type="button"
                onClick={handleHighlight}
                $active={editor.isActive('highlight')}
                $disabled={disabled}
                disabled={disabled}
                title="Highlight"
              >
                <Icon name="highlighter" size="small" />
              </StyledToolbarButton>
            )}
          </StyledToolbarGroup>

          {features.fontSize && (
            <>
              <StyledToolbarSeparator />
              <StyledToolbarGroup>
                <FontSizeDropdown
                  value={editor.getAttributes('textStyle').fontSize || '16px'}
                  onChange={handleFontSizeChange}
                  disabled={disabled}
                />
              </StyledToolbarGroup>
            </>
          )}

          {features.fontColor && (
            <>
              <StyledToolbarSeparator />
              <StyledToolbarGroup>
                <StyledToolbarButton
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowColorPicker(!showColorPicker)
                  }}
                  $active={showColorPicker}
                  $disabled={disabled}
                  disabled={disabled}
                  title="Text Color"
                  data-color-button
                >
                  <Icon name="palette" size="small" />
                </StyledToolbarButton>
                {showColorPicker && (
                  <ColorPicker
                    onColorSelect={handleColorChange}
                    onClose={() => setShowColorPicker(false)}
                  />
                )}
              </StyledToolbarGroup>
            </>
          )}

          {features.alignment && (
            <>
              <StyledToolbarSeparator />
              <StyledToolbarGroup>
                <StyledToolbarButton
                  type="button"
                  onClick={() => handleAlignment('left')}
                  $active={getCurrentAlignment() === 'left'}
                  $disabled={disabled}
                  disabled={disabled}
                  title="Align Left"
                >
                  <Icon name="align-left" size="small" />
                </StyledToolbarButton>
                <StyledToolbarButton
                  type="button"
                  onClick={() => handleAlignment('center')}
                  $active={getCurrentAlignment() === 'center'}
                  $disabled={disabled}
                  disabled={disabled}
                  title="Align Center"
                >
                  <Icon name="align-center" size="small" />
                </StyledToolbarButton>
                <StyledToolbarButton
                  type="button"
                  onClick={() => handleAlignment('right')}
                  $active={getCurrentAlignment() === 'right'}
                  $disabled={disabled}
                  disabled={disabled}
                  title="Align Right"
                >
                  <Icon name="align-right" size="small" />
                </StyledToolbarButton>
                <StyledToolbarButton
                  type="button"
                  onClick={() => handleAlignment('justify')}
                  $active={getCurrentAlignment() === 'justify'}
                  $disabled={disabled}
                  disabled={disabled}
                  title="Justify"
                >
                  <Icon name="align-justify" size="small" />
                </StyledToolbarButton>
              </StyledToolbarGroup>
            </>
          )}

          {features.lists && (
            <>
              <StyledToolbarSeparator />
              <StyledToolbarGroup>
                <StyledToolbarButton
                  type="button"
                  onClick={() =>
                    editor.chain().focus().toggleBulletList().run()
                  }
                  $active={editor.isActive('bulletList')}
                  $disabled={disabled}
                  disabled={disabled}
                  title="Bullet List"
                >
                  <Icon name="list" size="small" />
                </StyledToolbarButton>
                <StyledToolbarButton
                  type="button"
                  onClick={() =>
                    editor.chain().focus().toggleOrderedList().run()
                  }
                  $active={editor.isActive('orderedList')}
                  $disabled={disabled}
                  disabled={disabled}
                  title="Numbered List"
                >
                  <Icon name="list-ordered" size="small" />
                </StyledToolbarButton>
                <StyledToolbarButton
                  type="button"
                  onClick={() => editor.chain().focus().toggleTaskList().run()}
                  $active={editor.isActive('taskList')}
                  $disabled={disabled}
                  disabled={disabled}
                  title="Task List"
                >
                  <Icon name="list-checks" size="small" />
                </StyledToolbarButton>
              </StyledToolbarGroup>
            </>
          )}

          {(features.links || features.images || features.emojis) && (
            <>
              <StyledToolbarSeparator />
              <StyledToolbarGroup>
                {features.links && (
                  <StyledToolbarButton
                    type="button"
                    onClick={() => setShowLinkModal(true)}
                    $active={editor.isActive('link')}
                    $disabled={disabled}
                    disabled={disabled}
                    title="Add Link"
                  >
                    <Icon name="link" size="small" />
                  </StyledToolbarButton>
                )}
                {features.images && (
                  <StyledToolbarButton
                    type="button"
                    onClick={() => setShowImageUploader(true)}
                    $active={false}
                    $disabled={disabled}
                    disabled={disabled}
                    title="Add Image"
                  >
                    <Icon name="image" size="small" />
                  </StyledToolbarButton>
                )}
                {features.emojis && (
                  <>
                    <StyledToolbarButton
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowEmojiPicker(!showEmojiPicker)
                      }}
                      $active={showEmojiPicker}
                      $disabled={disabled}
                      disabled={disabled}
                      title="Add Emoji"
                      data-emoji-button
                    >
                      <Icon name="smile" size="small" />
                    </StyledToolbarButton>
                    {showEmojiPicker && (
                      <EmojiPicker
                        onEmojiSelect={handleEmojiInsert}
                        onClose={() => setShowEmojiPicker(false)}
                      />
                    )}
                  </>
                )}
              </StyledToolbarGroup>
            </>
          )}
          {features.undoRedo && (
            <>
              <StyledToolbarSeparator />
              <StyledToolbarGroup>
                <StyledToolbarButton
                  type="button"
                  onClick={() => editor.chain().focus().undo().run()}
                  $active={false}
                  $disabled={disabled || !editor.can().undo()}
                  disabled={disabled || !editor.can().undo()}
                  title="Undo (Ctrl+Z)"
                >
                  <Icon name="undo" size="small" />
                </StyledToolbarButton>
                <StyledToolbarButton
                  type="button"
                  onClick={() => editor.chain().focus().redo().run()}
                  $active={false}
                  $disabled={disabled || !editor.can().redo()}
                  disabled={disabled || !editor.can().redo()}
                  title="Redo (Ctrl+Y)"
                >
                  <Icon name="redo" size="small" />
                </StyledToolbarButton>
              </StyledToolbarGroup>
            </>
          )}
        </StyledToolbar>
      )}

      <StyledEditorArea
        $height={height}
        $minHeight={minHeight}
        $maxHeight={maxHeight}
      >
        <EditorContent editor={editor} placeholder={placeholder} />
      </StyledEditorArea>

      {showCharCount && (
        <StyledFooter>
          <StyledCharCount
            $exceeded={maxLength ? charCount > maxLength : false}
          >
            {charCount}
            {maxLength && ` / ${maxLength}`}
          </StyledCharCount>
        </StyledFooter>
      )}

      {showLinkModal && (
        <LinkModal
          onSubmit={handleAddLink}
          onClose={() => setShowLinkModal(false)}
        />
      )}

      {showImageUploader && (
        <ImageUploader
          onImageInsert={handleImageInsert}
          onClose={() => setShowImageUploader(false)}
        />
      )}
    </StyledEditorContainer>
  )
}
